import * as yup from 'yup';
import { ICustomFieldFormValue, ICustomFieldItem } from '@/types/customFields';

export const CONTACTS_PATH = 'contacts';

const getYupValidator = ({
    type,
    required,
}: {
    type: ICustomFieldFormValue['type'];
    required: boolean;
}) => {
    const requiredMsg = 'Este campo no puede estar vacío.';
    const regexMsg = 'El valor del campo es incorrecto.';
    switch (type) {
        case 'boolean':
            return !required
                ? yup.boolean()
                : yup.boolean().required(requiredMsg);

        case 'multi_select':
        case 'single_select':
            return !required
                ? yup.array().of(yup.boolean())
                : yup
                      .array()
                      .of(yup.boolean())
                      .test('at-least-one-true', requiredMsg, (arr = []) =>
                          arr.some((v) => v === true),
                      )
                      .required(requiredMsg);

        case 'text':
            return !required
                ? yup.string()
                : yup.string().required(requiredMsg);
        case 'number':
            return !required
                ? yup.number()
                : yup.number().required(requiredMsg);
        case 'date':
            return !required ? yup.date() : yup.date().required(requiredMsg);
        case 'alphanumeric':
            return !required
                ? yup.string().matches(/^[A-Za-z0-9]+$/, regexMsg)
                : yup
                      .string()
                      .matches(/^[A-Za-z0-9]+$/, regexMsg)
                      .required(requiredMsg);

        default: {
            return !required ? yup.mixed() : yup.mixed().required(requiredMsg);
        }
    }
};

const validationMessages = {
    requiredMessage: 'Este campo no puede estar vacío.',
    regexMessage: 'El valor del campo es incorrecto.',
    notOnlySpacesMessage: 'Este campo no puede contener solo espacios.',
    notSpacesOnStartOrEndMessage:
        'Este campo no puede comenzar ni terminar con espacios.',
    phoneRegexMessage:
        'El número de teléfono debe contener solamente entre 8 y 15 dígitos.',
    formatMessage: 'El número de teléfono no cumple con el formato requerido.',
};

export const contactValidationSchema = yup
    .object()
    .shape({
        name: yup
            .string()
            .matches(
                /^[a-zA-ZñáéíóúüÑÁÉÍÓÚÜ0-9\s\-/]+$/,
                'Solo se permiten caracteres alfanuméricos, espacios, guiones y barras',
            )
            .nullable()
            .optional(),
        lastName: yup
            .string()
            .matches(
                /^[a-zA-ZñáéíóúüÑÁÉÍÓÚÜ0-9\s\-/]+$/,
                'Solo se permiten caracteres alfanuméricos, espacios, guiones y barras',
            )
            .nullable()
            .optional(),
        company: yup
            .mixed()
            // .min(1, validationMessages.requiredMessage)
            .required(validationMessages.requiredMessage),
        phone: yup.string().when({
            is: (v: string) => !!v,
            then: (s) =>
                s
                    .matches(/^\d{8,15}$/, {
                        message: validationMessages.phoneRegexMessage,
                        excludeEmptyString: true,
                    })
                    .test(
                        'is-valid-phone',
                        validationMessages.formatMessage,
                        (value) => {
                            if (!value) return true;

                            return /^\d{8,15}$/.test(value);
                        },
                    )
                    .required(),
            otherwise: (v) => v.nullable(),
        }),
        email: yup.string().when({
            is: (v: string) => !!v,
            then: (s) =>
                s
                    .matches(
                        /^[A-Za-z\d._-]+@[A-Za-z\d.-]+\.[A-Za-z]{2,}$/,
                        'El valor del campo es incorrecto',
                    )
                    .test(
                        'no-invalid-chars',
                        'El valor del campo es incorrecto',
                        (value) =>
                            value
                                ? /^[A-Za-z\d._-]+@[A-Za-z\d.-]+\.[A-Za-z]{2,}$/.test(
                                      value,
                                  )
                                : false,
                    )
                    .test(
                        'no-spaces-start-end',
                        'El valor del campo es incorrecto',
                        (value) => {
                            if (!value) return false;
                            const trimmedValue = value.trim();

                            return trimmedValue === value; // No debe tener espacios al inicio o final
                        },
                    )
                    .test(
                        'no-special-start-end',
                        'El valor del campo es incorrecto',
                        (value) => {
                            if (!value) return false;
                            const firstChar = value.charAt(0);
                            const lastChar = value.charAt(value.length - 1);
                            const regex = /^[A-Za-z\d]$/;

                            return (
                                regex.test(firstChar) && regex.test(lastChar)
                            );
                        },
                    ),
            otherwise: (v) => v.nullable(),
        }),
        blocked: yup.boolean().optional(),
        blockedReason: yup.string().optional(),
        //Validación de campos personalizados
        customFields: yup
            .object()
            .shape({})
            .noUnknown(false)
            .test('dynamic-object', 'Campos inválidos', async function (obj) {
                if (!obj) return true;

                const errors: Record<string, string> = {};

                for (const [key, value] of Object.entries(obj)) {
                    try {
                        const currentValue = value as ICustomFieldItem & {
                            required?: boolean;
                        };

                        await getYupValidator({
                            type: currentValue?.content?.type,
                            required: !!currentValue?.required,
                        }).validate(currentValue.content.value);
                    } catch (err) {
                        if (err instanceof yup.ValidationError) {
                            errors[key] = err.message;
                        }
                    }
                }

                if (Object.keys(errors).length > 0) {
                    return this.createError({
                        message: JSON.stringify(errors),
                    });
                }

                return true;
            }),
    })
    .test(
        'at-least-one-contact',
        'Debe ingresar al menos un teléfono o correo electrónico.',
        function (values) {
            const { phone, email } = values;

            return !!(phone || email);
        },
    );
